import { ChangeDetectionStrategy, Component, Input, Output, EventEmitter, signal, computed, inject, ChangeDetectorRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FikoKnowledgeYonetici } from '../knowledge/FikoKnowledgeYonetici';
import { FikoIfadeMotoru } from '../knowledge/FikoIfadeMotoru';
import { FIKO_DIALOG_STIL } from '../fiko-dialog-stil';

const FONKSIYONLAR = ['sin', 'cos', 'tan', 'asin', 'acos', 'atan', 'atan2', 'sqrt', 'abs', 'min', 'max', 'pow', 'round', 'floor', 'ceil', 'log', 'exp', 'sign', 'if', 'PI', 'E'];

@Component({
  selector: 'fiko-fx-editor',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
  <div class="sd fx">
    <div class="sd-grup">
      <div class="sd-bas">Formül</div>
      <input class="fx-input" [ngModel]="metin()" (ngModelChange)="metin.set($event)"
             (keydown.enter)="tamam()" (keydown.escape)="iptalEt()" placeholder="ör: boy / 4 + etKalinlik" autofocus />
      <div class="fx-onizle" [class.fx-err]="!onizleme().ok">{{onizleme().deger}}</div>
    </div>
    <div class="sd-grup">
      <div class="sd-bas">Öneriler</div>
      <div class="fx-oneriler">
        <button *ngFor="let o of oneriler()" class="fx-oneri" (click)="oneriEkle(o)">{{o}}</button>
      </div>
    </div>
    <div class="sd-altbar">
      <button class="sd-btn sd-ok" [disabled]="!onizleme().ok" (click)="tamam()">✔ Tamam</button>
      <button class="sd-btn" (click)="iptalEt()">İptal</button>
      <button class="sd-btn sd-sil" (click)="coz()" title="Formülü kaldır, sabit değere dön">Çöz</button>
    </div>
  </div>
  `,
  styles: [FIKO_DIALOG_STIL + `
    .fx{min-width:255px;max-width:330px;}
    .fx-input{width:100%;box-sizing:border-box;background:rgba(0,0,0,.4);color:#fff;font-weight:600;border:1px solid rgba(0,200,255,.4);border-radius:5px;padding:5px 7px;outline:none;font-family:monospace;}
    .fx-input:focus{border-color:#00d0ff;background:rgba(0,60,80,.4);}
    .fx-onizle{margin-top:5px;color:#34d399;font-family:monospace;font-size:11px;}
    .fx-onizle.fx-err{color:#fca5a5;}
    .fx-oneriler{display:flex;flex-wrap:wrap;gap:4px;max-height:104px;overflow:auto;}
    .fx-oneri{background:rgba(20,40,60,.7);border:1px solid rgba(0,200,255,.25);color:#cfe6f5;border-radius:10px;padding:2px 9px;cursor:pointer;font-size:11px;}
    .fx-oneri:hover{background:rgba(0,150,200,.4);border-color:#00d0ff;color:#fff;}
  `],
})
export class FikoFxEditorComponent implements OnInit {
  private cd = inject(ChangeDetectorRef);
  @Input() knowledge!: FikoKnowledgeYonetici;
  @Input() set formul(v: string) { this.metin.set(v || ''); }
  @Input() haricAd?: string;
  /** Dialog modu: paramId verilirse Tamam doğrudan uygular + kapat() çağırır. */
  @Input() paramId?: string;
  @Input() kapat: () => void = () => {};
  @Output() kaydet = new EventEmitter<string>();
  @Output() iptal = new EventEmitter<void>();

  metin = signal('');
  ngOnInit() {
    if (this.paramId) { const p = this.knowledge?.depo.getId(this.paramId); if (p) { this.metin.set(p.formul || ''); this.haricAd = p.ad; } }
  }
  onizleme = computed(() => {
    const f = this.metin().trim();
    if (!f) return { ok: true, deger: '(boş → sabit değer)' };
    try {
      const coz = (ad: string) => this.knowledge?.depo.getAd(ad)?.deger;
      const r = FikoIfadeMotoru.degerlendir(f, coz);
      return { ok: true, deger: '= ' + (typeof r.deger === 'number' ? +(+r.deger).toFixed(4) : r.deger) };
    } catch (e: any) { return { ok: false, deger: e?.message || 'Hata' }; }
  });
  oneriler = computed(() => {
    const t = this.sonToken();
    const adlar = (this.knowledge?.depo.parametreler || []).map(p => p.ad).filter(a => a !== this.haricAd);
    const hepsi = [...adlar, ...FONKSIYONLAR];
    if (!t) return hepsi.slice(0, 16);
    const tl = t.toLocaleLowerCase('tr');
    return hepsi.filter(a => a.toLocaleLowerCase('tr').includes(tl)).slice(0, 16);
  });
  private basitMi(ad: string): boolean { return /^[A-Za-z_][A-Za-z0-9_]*$/.test(ad); }
  /** Açık backtick varsa onun içeriği, yoksa son basit tanımlayıcı — autocomplete filtresi için. */
  private sonToken(): string {
    const s = this.metin();
    const tek = (s.match(/`/g) || []).length % 2 === 1;   // açık (kapanmamış) backtick var mı
    if (tek) return s.slice(s.lastIndexOf('`') + 1);
    const m = s.match(/[A-Za-z_][A-Za-z0-9_]*$/); return m ? m[0] : '';
  }
  oneriEkle(ad: string) {
    const isFn = FONKSIYONLAR.includes(ad);
    const ekle = isFn ? (['PI', 'E'].includes(ad) ? ad : ad + '(') : (this.basitMi(ad) ? ad : '`' + ad + '`');
    // son token'ı sil: açık backtick bloğu ya da basit tanımlayıcı
    this.metin.update(s => s.replace(/(`[^`]*|[A-Za-z_][A-Za-z0-9_]*)?$/, '') + ekle);
    this.cd.markForCheck();
  }
  private uygula(formul: string) {
    if (this.paramId) { this.knowledge?.parametreGuncelle(this.paramId, { formul: formul || undefined }); this.kapat(); }
    else { this.kaydet.emit(formul); }
  }
  tamam() { if (this.onizleme().ok) this.uygula(this.metin().trim()); }
  coz() { this.uygula(''); }
  iptalEt() { if (this.paramId) this.kapat(); else this.iptal.emit(); }
}
